import { Component, Inject, OnInit, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

import { FirebaseAuthService } from '../services/firebase-auth.service';
import { AUTH_CONFIG, AuthConfig } from '../models/auth-token-settings';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RestorePasswordComponent } from './restore-password/restore-password.component';

@Component({
  selector: 'dc-login',
  templateUrl: './dc-login.component.html',
  styleUrls: ['./dc-login.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule, ButtonModule, InputGroupModule, InputTextModule, MessageModule, RestorePasswordComponent],
  providers: [DialogService],
})
export class DcLoginComponent implements OnInit {
  loading = signal(false);
  error = signal<string | null>(null);
  loginForm: FormGroup;
  ref: DynamicDialogRef | undefined;

  constructor(
    @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
    protected fbAuthService: FirebaseAuthService,
    protected router: Router,
    private fb: FormBuilder,
    public dialogService: DialogService,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  ngOnInit() {
    console.log('AuthConfig: Variables', this.authConfig);
  }

  async login() {
    if (this.loginForm.invalid) return;

    this.loading.set(true);
    this.error.set(null);
    try {
      const { email, password } = this.loginForm.value;
      await this.fbAuthService.signWithEmailPassword(email, password);
    } catch (error: any) {
      this.error.set(error.message);
      console.error('Login error:', error);
    } finally {
      this.loading.set(false);
    }
  }

  public async loginWithGoogle() {
    await this.handleSocialLogin(() => this.fbAuthService.loginWithGoogle(), 'Google');
  }

  async loginWithFacebook() {
    await this.handleSocialLogin(() => this.fbAuthService.signWithProvider('facebook'), 'Facebook');
  }

  public restorePassword() {
    this.ref = this.dialogService.open(RestorePasswordComponent, {
      header: 'Restore Password',
      width: '300px',
    });

    this.ref.onClose.subscribe(async (email: string) => {
      if (email) {
        this.loading.set(true);
        this.error.set(null);
        try {
          await this.fbAuthService.sendPasswordResetEmail(email);
          // You can add a success message here, maybe with a Toast
        } catch (err: any) {
          this.error.set(err?.message || err);
          console.error(err);
        } finally {
          this.loading.set(false);
        }
      }
    });
  }

  public goToSignup() {
    const uri = this.authConfig?.settings?.signupRedirectUri || '/auth/signup';
    this.router.navigate([uri]);
  }

  public async selectAppleLogin() {
    await this.handleSocialLogin(() => this.fbAuthService.selectAppleLogin(), 'Apple');
  }

  private async handleSocialLogin(loginAction: () => Promise<any>, provider: string) {
    this.loading.set(true);
    this.error.set(null);
    try {
      await loginAction();
    } catch (error: any) {
      this.error.set(error.message);
      console.error(`${provider} login error:`, error);
    } finally {
      this.loading.set(false);
    }
  }
}
